import React, { useState } from "react";
import { Button, ButtonGroup, Dialog, DialogActions, Pagination, Stack, TableFooter, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { DialogContent } from "@mui/material";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useWarehouse } from "../../hooks/useWarehouse";
import { useToast } from "@shared/hooks/useToast";
import WarehouseInventoryCountRow from "./WarehouseInventoryCountRow";
import { APP_CONFIG } from "../../../../config/app.config";
import ProductsFilter from "@shared/ui/filters/ProductsFilter";
import { useProductsFilter } from "@features/products/hooks/useProductsFilter";
import { InventoryMutation } from "@domain/inventory/models/InventoryMutation";
import { MutationType } from "@domain/inventory/enums/MutationType.enum";
import DateTimeSelector from "./DateTimeSelector";
import ActiveFilters from "@shared/ui/filters/ActiveFilters";
import { EmptyState } from "@shared/ui/common";
import DialogHeader from "@shared/ui/dialog/DialogHeader";

interface WarehouseInventoryCountProps {
    warehouseId: string;
    onClose: () => void;
    onSubmit: (counts: InventoryMutation[]) => void;
    open: boolean;
}

const WarehouseInventoryCount = ({ warehouseId, onClose, onSubmit, open }: WarehouseInventoryCountProps) => {
    const toast = useToast();

    const { filterCriteria, updateFilter } = useProductsFilter();

    const [showPurchasable, setShowPurchasable] = useState(true);

    const { inventory, page, totalPages, submitMutation } = useWarehouse(warehouseId, { ...filterCriteria, active: true, purchasable: showPurchasable });

    const [counts, setCounts] = useState<Record<string, number | null>>({});

    const handleQuantityChange = (productId: string, quantity?: number | null) => {
        setCounts(prev => ({
            ...prev,
            [productId]: quantity ?? null
        }));
    };

    const handleSubmitMutations = async () => {
        if (!counts) return;

        try {
            const mutationsToSubmit = Object.entries(counts)
                .filter(([_, quantity]) => quantity !== undefined && quantity !== null && !isNaN(quantity))
                .map(([productId, quantity]) => ({
                    productId,
                    quantity: quantity,
                    warehouseId,
                    type: MutationType.SYNC
                }));

            const mutations = mutationsToSubmit
                .map(mutation => {
                    const product = inventory?.find((i) => i.product.id === mutation.productId)?.product ?? null;
                    if (!product || mutation.quantity === null) return null;

                    return {
                        warehouse: { id: warehouseId } as any,
                        product,
                        quantity: mutation.quantity,
                        date: new Date(),
                        type: MutationType.SYNC,
                        referenceType: 'manual'
                    };
                })
                .filter((mutation): mutation is NonNullable<typeof mutation> => mutation !== null);

            await submitMutation({
                mutations,
                date: new Date()
            });

            setCounts({});

            toast.success('Inventory counts submitted successfully');
            onClose();
        } catch (error) {
            toast.error('Failed to submit inventory counts');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">

            <DialogHeader   >
                <Stack direction="row" spacing={2} width="100%" justifyContent="space-between">
                    <Stack direction="row" spacing={2}>
                        <ProductsFilter />

                        <ActiveFilters />
                    </Stack>

                    <ButtonGroup variant="outlined">
                        <Button
                            onClick={() => setShowPurchasable(true)}
                            color='info'
                            variant={showPurchasable ? 'contained' : 'outlined'}
                            size='small'
                        >
                            Purchasable
                        </Button>
                        <Button
                            onClick={() => setShowPurchasable(false)}
                            color='info'
                            variant={!showPurchasable ? 'contained' : 'outlined'}
                            size='small'
                        >
                            Assembled
                        </Button>

                    </ButtonGroup>
                </Stack>
            </DialogHeader>
            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                    <Table>


                        {inventory && (

                            <TableBody>
                                {inventory.map((item) => (
                                    <WarehouseInventoryCountRow
                                        key={item.product.id}
                                        product={item.product}
                                        quantity={item.quantity}
                                        handleQuantityChange={handleQuantityChange}
                                    />
                                ))}

                                {inventory.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                                            <EmptyState
                                                title="No products found"
                                                description="Please filter your products to see the inventory."
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}


                            </TableBody>

                        )}


                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions sx={{ borderTop: `1px solid ${APP_CONFIG.ui.theme.colors.border}`, p: 3, justifyContent: 'space-between' }}>
                <Button onClick={onClose} variant="outlined" color="error">Cancel</Button>


                <Pagination
                    count={totalPages || 0}
                    page={page}
                    onChange={(_, value) => updateFilter({ ...filterCriteria, page: value })}
                />

                <Button
                    variant="contained"
                    onClick={handleSubmitMutations}
                >
                    Submit Counts
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WarehouseInventoryCount;